import { ShoppingCart } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Crosshair, Search, BookOpen, ExternalLink, Heart, ChevronDown, ChevronRight } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import api, { recipesAPI, firearmsAPI, caliberOptionsAPI, loadsAPI, firearmTypesAPI, firingSessionsAPI, uploadsAPI, usersAPI, conversationsAPI, purchasesAPI, cartridgeAbbrev, cartridgesStatic } from '../services/api';
import { formatLocalDate } from '../services/date';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import RecipeModal from '../components/RecipeModal';

const Firearms = () => {
  const [firearms, setFirearms] = useState([]);
  const [firearmTypes, setFirearmTypes] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loads, setLoads] = useState([]);
  const [caliberOptions, setCaliberOptions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedPrimary, setSelectedPrimary] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFirearm, setEditingFirearm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [componentAvailability, setComponentAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [customCaliber, setCustomCaliber] = useState('');
  // Mirror Recipes modal actions locally
  const [showAddLoadsFromRecipe, setShowAddLoadsFromRecipe] = useState(false);
  const [showSessionFromRecipe, setShowSessionFromRecipe] = useState(false);
  const [recipeCaseBatches, setRecipeCaseBatches] = useState([]);
  const [imagesByRecipe, setImagesByRecipe] = useState({});
  const [showShareRecipe, setShowShareRecipe] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseComponent, setPurchaseComponent] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' });
  const [recipeStats, setRecipeStats] = useState({});
  // velocities will be managed with react-hook-form field array
  const [firearmFiles, setFirearmFiles] = useState([]);
  const [imagesByFirearm, setImagesByFirearm] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [collapsedRecipes, setCollapsedRecipes] = useState({});
  const [collapsedFiringSessions, setCollapsedFiringSessions] = useState({});
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const watchCaliber = watch('caliber');
  const selectedSecondary = watch('intended_calibers') || [];
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);
  const [showSecondaryOther, setShowSecondaryOther] = useState(false);
  const [secondaryOtherValue, setSecondaryOtherValue] = useState('');
  const [secondaryFilter, setSecondaryFilter] = useState('');
  const secondaryRef = useRef(null);
  const { register: registerAddLoad, handleSubmit: handleSubmitAddLoad, reset: resetAddLoad, formState: { errors: errorsAddLoad } } = useForm();
  const { register: registerSession, handleSubmit: handleSubmitSession, reset: resetSession, control: controlSession, formState: { errors: errorsSession } } = useForm({ defaultValues: { velocities: [] } });
  const { fields: fieldsSession, append: appendSessionVelocity, remove: removeSessionVelocity } = useFieldArray({ control: controlSession, name: 'velocities' });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('firingSessionChanged', handler);
    return () => window.removeEventListener('firingSessionChanged', handler);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [firearmsRes, typesRes, recipesRes, loadsRes, caliberOptionsRes, sessionsRes] = await Promise.all([
        firearmsAPI.getAll(),
        firearmTypesAPI.getAll(),
        recipesAPI.getAll(),
        loadsAPI.getAll(),
        caliberOptionsAPI.getAll(),
        firingSessionsAPI.getAll(),
      ]);
      setFirearms(firearmsRes.data);
      setFirearmTypes(typesRes.data);
      setRecipes(recipesRes.data);
      setLoads(loadsRes.data);
      // Merge static cartridges with custom cartridges from backend
      const staticList = cartridgesStatic();
      const staticSet = new Set(staticList.map(o => (o.value || '').trim().toLowerCase()));
      const normalize = (v) => (v || '').trim().replace(/^\./, '').trim();
      const customList = (caliberOptionsRes.data || [])
        .map(o => ({ id: `custom-${o.id}`, value: normalize(o.value) }))
        .filter(o => o.value && !o.value.startsWith('.'));
      const seen = new Set();
      const merged = [];
      [...staticList, ...customList].forEach(o => {
        const key = normalize(o.value).toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        // prefer static label if present
        if (staticSet.has(key)) {
          const found = staticList.find(s => (s.value || '').trim().toLowerCase() === key);
          if (found) { merged.push(found); return; }
        }
        merged.push({ id: o.id, value: normalize(o.value) });
      });
      merged.sort((a, b) => (a.value || '').localeCompare(b.value || '', undefined, { sensitivity: 'base', numeric: true }));
      setCaliberOptions(merged);
      setSessions(sessionsRes.data);
      // Load images for firearms to show previews on cards
      try {
        const pairs = await Promise.all(
          (firearmsRes.data || []).map(async (f) => {
            try {
              const res = await uploadsAPI.list('firearm', f.id);
              return [f.id, Array.isArray(res.data) ? res.data : []];
            } catch {
              return [f.id, []];
            }
          })
        );
        const map = {};
        pairs.forEach(([id, list]) => { map[id] = list; });
        setImagesByFirearm(map);
      } catch {
        setImagesByFirearm({});
      }
    } catch (error) {
      console.error('Error loading firearms:', error);
      toast.error('Failed to load firearms');
    } finally {
      setLoading(false);
    }
  };

  const reloadFirearmImages = async (firearmId) => {
    try {
      const res = await uploadsAPI.list('firearm', firearmId);
      setImagesByFirearm(prev => ({ ...prev, [firearmId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setImagesByFirearm(prev => ({ ...prev, [firearmId]: [] }));
    }
  };

  const onSubmit = async (data) => {
    try {
      // Handle intended_calibers - convert array to JSON string
      if (data.intended_calibers) {
        data.intended_calibers = JSON.stringify(data.intended_calibers);
      }
      
      if (editingFirearm) {
        await firearmsAPI.update(editingFirearm.id, data);
        // Upload any staged files for this firearm on update
        if (Array.isArray(firearmFiles) && firearmFiles.length > 0) {
          try {
            await uploadsAPI.upload('firearm', editingFirearm.id, firearmFiles);
            toast.success('Images uploaded');
          } catch {
            toast.error('Failed to upload images');
          }
          setFirearmFiles([]);
          await reloadFirearmImages(editingFirearm.id);
        }
        toast.success('Firearm updated successfully');
        setEditingFirearm(null);
      } else {
        const res = await firearmsAPI.create(data);
        const newId = res?.data?.id;
        // If files were selected during create, upload them now that we have an ID
        if (newId && Array.isArray(firearmFiles) && firearmFiles.length > 0) {
          try {
            await uploadsAPI.upload('firearm', newId, firearmFiles);
            toast.success('Images uploaded');
          } catch {
            toast.error('Failed to upload images');
          }
          setFirearmFiles([]);
          await reloadFirearmImages(newId);
        }
        toast.success('Firearm added successfully');
      }
      setShowForm(false);
      reset();
      loadData();
    } catch (error) {
      console.error('Error saving firearm:', error);
      toast.error(editingFirearm ? 'Failed to update firearm' : 'Failed to add firearm');
    }
  };

  const handleEdit = (firearm) => {
    setEditingFirearm(firearm);
    setValue('manufacturer', firearm.manufacturer);
    setValue('model', firearm.model);
    setValue('caliber', firearm.caliber);
    setValue('serial_number', firearm.serial_number || '');
    // Ensure subtype dropdown selects the current type id
    setValue('firearm_type_id', firearm.firearm_type?.id || '');
    setSelectedPrimary(firearm.firearm_type?.category || '');
    setValue('notes', firearm.notes || '');
    
    // Handle intended_calibers
    if (firearm.intended_calibers) {
      try {
        const calibers = JSON.parse(firearm.intended_calibers);
        setValue('intended_calibers', calibers);
      } catch (error) {
        console.error('Error parsing intended_calibers:', error);
        setValue('intended_calibers', []);
      }
    } else {
      setValue('intended_calibers', []);
    }
    
    setShowForm(true);
  };

  

  const handleCancel = () => {
    setShowForm(false);
    setEditingFirearm(null);
    reset();
    setCustomCaliber('');
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (secondaryRef.current && !secondaryRef.current.contains(e.target)) {
        setShowSecondaryDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggleSecondary = (val) => {
    const current = Array.isArray(selectedSecondary) ? selectedSecondary : [];
    if (current.includes(val)) {
      setValue('intended_calibers', current.filter(v => v !== val));
    } else {
      setValue('intended_calibers', [...current, val]);
    }
  };

  const addSecondaryOther = async () => {
    let name = (secondaryOtherValue || '').trim();
    if (name.startsWith('.')) name = name.slice(1).trim();
    if (!name) return;
    try {
      await api.post('/cartridges', { cartridge: name });
      // Refresh options
      const res = await caliberOptionsAPI.getAll();
      const staticList = cartridgesStatic();
      const customList = (res.data || []).map(o => ({ id: `custom-${o.id}`, value: o.value }));
      const seen = new Set();
      const merged = [];
      [...staticList, ...customList].forEach(o => {
        const key = (o.value || '').toLowerCase();
        if (!seen.has(key)) { seen.add(key); merged.push(o); }
      });
      setCaliberOptions(merged);
      // Select it for this firearm's secondary list
      const current = Array.isArray(selectedSecondary) ? selectedSecondary : [];
      if (!current.includes(name)) setValue('intended_calibers', [...current, name]);
      setSecondaryOtherValue('');
      setShowSecondaryOther(false);
      setShowSecondaryDropdown(false);
      toast.success('Custom cartridge added');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add cartridge');
    }
  };

  // Resolve recipe object for a given load id using grouped loads structure
  const getRecipeByLoadId = (loadId) => {
    try {
      const lid = Number(loadId);
      for (const group of (loads || [])) {
        const ids = Array.isArray(group?.batch_ids) && group.batch_ids.length > 0 ? group.batch_ids : [group?.id];
        if (ids.some(id => Number(id) === lid)) {
          const rid = group?.recipe?.id;
          if (rid != null) {
            const full = (recipes || []).find(r => Number(r.id) === Number(rid));
            return full || group?.recipe || null;
          }
          return group?.recipe || null;
        }
      }
    } catch {}
    return null;
  };

  const addCustomCaliber = async (caliberName) => {
    try {
      const { data } = await api.post('/cartridges', { cartridge: caliberName });
      toast.success('Custom cartridge added');
      // Reload options
      loadData();
      return data;
    } catch (error) {
      console.error('Error adding custom cartridge:', error);
      toast.error('Failed to add cartridge');
    }
  };

  const getRecipesForFirearm = (firearmId) => {
    return recipes.filter(recipe => {
      if (!recipe.intended_firearms) return false;
      try {
        const intendedFirearms = JSON.parse(recipe.intended_firearms);
        return intendedFirearms.includes(firearmId.toString());
      } catch (error) {
        return false;
      }
    });
  };

  const handleRecipeClick = async (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeModal(true);
    setLoadingAvailability(true);
    
    try {
      const availability = await getComponentAvailability(recipe.id);
      setComponentAvailability(availability);
      // Load recipe stats (current CPR, total spent, component breakdown)
      try {
        const { data } = await api.get(`/recipes/${recipe.id}/cost`);
        setRecipeStats(prev => ({
          ...prev,
          [recipe.id]: {
            currentCostPerRound: data?.total_cost_per_round ?? null,
            totalSpent: data?.total_spent ?? 0,
            componentBreakdown: data?.component_breakdown ?? []
          }
        }));
      } catch {}
    } catch (error) {
      console.error('Error loading component availability:', error);
      setComponentAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
    // Load recipe images for previews
    try {
      const res = await uploadsAPI.list('recipe', recipe.id);
      setImagesByRecipe(prev => ({ ...prev, [recipe.id]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setImagesByRecipe(prev => ({ ...prev, [recipe.id]: [] }));
    }
  };

  const closeRecipeModal = () => {
    setShowRecipeModal(false);
    setSelectedRecipe(null);
    setComponentAvailability([]);
    setLoadingAvailability(false);
  };

  // Helpers/actions to match Recipes modal behavior
  const getCompatibleFirearmsForRecipe = (recipe) => {
    const cart = (recipe?.cartridge || '').trim().toLowerCase();
    return firearms.filter(f => (f.caliber || '').trim().toLowerCase() === cart);
  };

  const openAddLoadsFromRecipe = async () => {
    if (!selectedRecipe) return;
    try {
      const res = await loadsAPI.getCaseBatches(selectedRecipe.id);
      setRecipeCaseBatches(res.data || []);
    } catch {
      setRecipeCaseBatches([]);
    } finally {
      setShowAddLoadsFromRecipe(true);
    }
  };

  const onSubmitAddLoadsFromRecipe = async (data) => {
    if (!selectedRecipe) return;
    try {
      await loadsAPI.create({
        recipe_id: selectedRecipe.id,
        case_batch_id: data.case_batch_id,
        quantity_created: Number(data.quantity_created || 0),
        date_created: data.date_created,
        notes: data.notes || ''
      });
      toast.success('Load batch created');
      setShowAddLoadsFromRecipe(false);
      resetAddLoad();
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create load');
    }
  };

  const openSessionFromRecipe = () => {
    if (!selectedRecipe) return;
    resetSession({ velocities: [] });
    setShowSessionFromRecipe(true);
  };

  const onSubmitSessionFromRecipe = async (data) => {
    try {
      const res = await firingSessionsAPI.create({
        firearm_id: Number(data.firearm_id),
        load_id: Number(data.load_id),
        location: data.location,
        temperature: data.temperature ? Number(data.temperature) : undefined,
        rounds_fired: Number(data.rounds_fired),
        session_date: data.session_date,
        notes: data.notes || '',
        velocities: (data.velocities || []).map(v => v && v.velocity ? parseFloat(v.velocity) : null).filter(v => v !== null)
      });
      toast.success('Firing session recorded');
      const loadForEvent = loads.find((l) => String(l.id) === String(data.load_id));
      const recipeIdForEvent = loadForEvent?.recipe?.id ?? selectedRecipe?.id ?? null;
      window.dispatchEvent(new CustomEvent('firingSessionChanged', {
        detail: { action: 'created', sessionId: res?.data?.id, loadId: data.load_id, recipeId: recipeIdForEvent }
      }));
      setShowSessionFromRecipe(false);
      resetSession({ velocities: [] });
      loadData();
    } catch {
      toast.error('Failed to record firing session');
    }
  };

  // Image/PDF uploads for recipe modal
  const handleUploadRecipe = async (recipeId, files) => {
    if (!files || files.length === 0) return;
    try {
      await uploadsAPI.upload('recipe', recipeId, Array.from(files));
      const res = await uploadsAPI.list('recipe', recipeId);
      setImagesByRecipe(prev => ({ ...prev, [recipeId]: res.data || [] }));
      toast.success('Files uploaded');
    } catch (e) {
      toast.error('Failed to upload files');
    }
  };

  // Image uploads for firearm form
  const handlePickFirearmFiles = (fileList) => {
    const chosen = Array.from(fileList || []);
    if (chosen.length === 0) return;
    // If editing, upload immediately; if creating, stage files and upload after save
    if (editingFirearm && editingFirearm.id) {
      uploadsAPI.upload('firearm', editingFirearm.id, chosen)
        .then(() => {
          toast.success('Images uploaded');
          reloadFirearmImages(editingFirearm.id);
        })
        .catch(() => {
          toast.error('Failed to upload images');
        });
    } else {
      setFirearmFiles(chosen);
      toast.success('Images will upload after you save the firearm');
    }
  };

  const removeFirearmImage = async (firearmId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesByFirearm(prev => ({ ...prev, [firearmId]: (prev[firearmId] || []).filter(i => i.id !== imageId) }));
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete file');
    }
  };

  const removeRecipeImage = async (recipeId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesByRecipe(prev => ({ ...prev, [recipeId]: (prev[recipeId] || []).filter(i => i.id !== imageId) }));
      toast.success('File deleted');
    } catch (e) {
      toast.error('Failed to delete file');
    }
  };

  const getComponentDetails = (recipe, componentType) => {
    if (!recipe.components) return null;
    const component = recipe.components.find(c => 
      c.component_type.toLowerCase() === componentType.toLowerCase()
    );
    return component?.component || null;
  };

  // Purchase modal helpers (match Recipes modal behavior)
  const handlePurchaseCalc = (field, value) => {
    const quantity = parseFloat(field === 'quantity' ? value : purchaseForm.quantity) || 0;
    const total = parseFloat(field === 'total_cost' ? value : purchaseForm.total_cost) || 0;
    const unit = parseFloat(field === 'unit_cost' ? value : purchaseForm.unit_cost) || 0;
    const next = { ...purchaseForm };
    if (field === 'quantity') next.quantity = value;
    if (field === 'total_cost') next.total_cost = value;
    if (field === 'unit_cost') next.unit_cost = value;
    if (field === 'total_cost') {
      const calcUnit = quantity > 0 ? (total / quantity) : 0;
      next.unit_cost = isFinite(calcUnit) ? calcUnit.toFixed(2) : '0.00';
    } else if (field === 'unit_cost' || field === 'quantity') {
      const calcTotal = quantity * unit;
      next.total_cost = isFinite(calcTotal) ? calcTotal.toFixed(2) : '0.00';
    }
    setPurchaseForm(next);
  };

  const submitPurchaseFromRecipe = async () => {
    try {
      if (!purchaseComponent || !selectedRecipe) return;
      await purchasesAPI.create({
        component_id: purchaseComponent.id,
        quantity: Number(purchaseForm.quantity || 0),
        total_cost: Number(purchaseForm.total_cost || 0),
        unit_cost: Number(purchaseForm.unit_cost || 0),
        purchase_date: purchaseForm.purchase_date,
        purchase_location: purchaseForm.purchase_location,
        website: purchaseForm.website,
        notes: purchaseForm.notes||''
      });
      toast.success('Purchase recorded');
      setShowPurchaseModal(false);
      // Refresh availability and stats in the open recipe modal
      try {
        const availability = await getComponentAvailability(selectedRecipe.id);
        setComponentAvailability(availability);
      } catch {}
      try {
        const { data } = await api.get(`/recipes/${selectedRecipe.id}/cost`);
        setRecipeStats(prev => ({
          ...prev,
          [selectedRecipe.id]: {
            currentCostPerRound: data?.total_cost_per_round ?? null,
            totalSpent: data?.total_spent ?? 0,
            componentBreakdown: data?.component_breakdown ?? []
          }
        }));
      } catch {}
      // Also reload loads to reflect new availability/costs
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to record purchase');
    }
  };

  // Aggregate per-firearm velocity stats for a given recipe using sessions list
  const getVelocityByFirearmForRecipe = (recipeId) => {
    // Map loadId->recipeId from grouped loads structure
    const loadToRecipe = new Map();
    (loads || []).forEach(group => {
      const rid = group?.recipe?.id;
      (group?.batch_ids || []).forEach(bid => { loadToRecipe.set(bid, rid); });
    });
    const list = (sessions || []).filter(s => loadToRecipe.get(s.load?.id) === recipeId);
    const byFirearm = {};
    list.forEach(s => {
      const stats = s.velocity_statistics;
      if (!stats || !s.firearm) return;
      const id = s.firearm.id;
      if (!byFirearm[id]) {
        byFirearm[id] = {
          label: `${s.firearm.manufacturer} ${s.firearm.model}`,
          totalCount: 0,
          sumMeans: 0,
          min: Number.POSITIVE_INFINITY,
          max: Number.NEGATIVE_INFINITY,
          withinSS: 0
        };
      }
      const g = byFirearm[id];
      const n = stats.count || 0;
      if (n <= 0) return;
      const mean = Number(stats.average || 0);
      const sd = Number(stats.standard_deviation || 0);
      g.totalCount += n;
      g.sumMeans += mean * n;
      g.min = Math.min(g.min, Number(stats.min || mean));
      g.max = Math.max(g.max, Number(stats.max || mean));
      g.withinSS += (n > 1 ? (n - 1) * (sd * sd) : 0);
    });
    return Object.values(byFirearm).map(g => {
      const mean = g.totalCount > 0 ? g.sumMeans / g.totalCount : 0;
      const variance = g.totalCount > 1 ? (g.withinSS / (g.totalCount - 1)) : 0;
      return {
        label: g.label,
        average: Math.round(mean),
        count: g.totalCount,
        min: isFinite(g.min) ? Math.round(g.min) : null,
        max: isFinite(g.max) ? Math.round(g.max) : null,
        sd: Math.round(Math.sqrt(Math.max(variance, 0))),
        es: (isFinite(g.min) && isFinite(g.max)) ? Math.round(g.max - g.min) : null
      };
    });
  };

  const getRemainingLoadsForRecipe = (recipeId) => {
    const recipeLoads = loads.filter(load => load.recipe.id === recipeId);
    return recipeLoads.reduce((total, load) => total + load.remaining_quantity, 0);
  };

  const getTotalLoadsCreated = (recipeId) => {
    const recipeLoads = loads.filter(load => load.recipe.id === recipeId);
    return recipeLoads.reduce((total, load) => total + load.quantity_created, 0);
  };

  const getTotalSpent = (recipeId) => {
    const recipeLoads = loads.filter(load => load.recipe.id === recipeId);
    return recipeLoads.reduce((total, load) => total + load.total_cost, 0);
  };

  const getAverageCostPerLoad = (recipeId) => {
    const recipeLoads = loads.filter(load => load.recipe.id === recipeId);
    if (recipeLoads.length === 0) return 0;
    
    const totalCost = recipeLoads.reduce((total, load) => total + load.total_cost, 0);
    const totalLoadsCreated = recipeLoads.reduce((total, load) => total + load.quantity_created, 0);
    
    return totalLoadsCreated > 0 ? totalCost / totalLoadsCreated : 0;
  };

  const getComponentAvailability = async (recipeId) => {
    try {
      const { data } = await api.get(`/recipes/${recipeId}/cost`);
      return data.component_breakdown || [];
    } catch (error) {
      console.error('Error fetching component availability:', error);
      return [];
    }
  };

  const filteredFirearms = firearms.filter(firearm =>
    firearm.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    firearm.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    firearm.caliber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Firearms</h1>
          <p className="text-gray-600 mt-2">Manage your firearm collection</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Firearm</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search firearms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Add Firearm Form */}
      {showForm && (
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingFirearm ? 'Edit Firearm' : 'Add New Firearm'}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Manufacturer</label>
              <input
                type="text"
                {...register('manufacturer', { required: 'Manufacturer is required' })}
                className="input"
                placeholder="e.g., Smith & Wesson"
              />
              {errors.manufacturer && (
                <p className="text-red-500 text-sm mt-1">{errors.manufacturer.message}</p>
              )}
            </div>

            <div>
              <label className="label">Model</label>
              <input
                type="text"
                {...register('model', { required: 'Model is required' })}
                className="input"
                placeholder="e.g., Model 686"
              />
              {errors.model && (
                <p className="text-red-500 text-sm mt-1">{errors.model.message}</p>
              )}
            </div>

            <div>
              <label className="label">Primary Caliber</label>
              <select
                {...register('caliber', { required: 'Primary caliber is required' })}
                className="input"
              >
                <option value="">Select caliber...</option>
                {caliberOptions.map(option => (
                  <option key={option.id} value={option.value}>{option.value}</option>
                ))}
                <option value="other">Other</option>
              </select>
              {errors.caliber && (
                <p className="text-red-500 text-sm mt-1">{errors.caliber.message}</p>
              )}
              {watchCaliber === 'other' && (
                <div className="mt-2">
              <input
                type="text"
                    value={customCaliber}
                    onChange={(e) => setCustomCaliber(e.target.value)}
                className="input"
                    placeholder="Enter custom caliber..."
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (customCaliber) {
                        const newOption = await addCustomCaliber(customCaliber);
                        if (newOption) {
                          setCustomCaliber('');
                        }
                      }
                    }}
                    className="btn btn-secondary mt-2"
                  >
                    Add Custom Caliber
                  </button>
                </div>
              )}
            </div>

            <div className="md:col-span-2" ref={secondaryRef}>
              <label className="label">Secondary Calibers</label>
              <div className="relative mt-2">
                <button
                  type="button"
                  className="input w-full flex items-center justify-between"
                  onClick={() => setShowSecondaryDropdown(v => !v)}
                >
                  <span>{selectedSecondary.length > 0 ? `${selectedSecondary.length} selected` : 'Select calibers...'}</span>
                  <span className="text-gray-400">▾</span>
                </button>
                {showSecondaryDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow max-h-64 overflow-auto">
                    <div className="p-2 border-b border-gray-100">
                      <input className="input w-full" placeholder="Filter..." value={secondaryFilter} onChange={(e)=>setSecondaryFilter(e.target.value)} />
                    </div>
                    {caliberOptions
                      .filter(o => (o.value || '').toLowerCase().includes(secondaryFilter.toLowerCase()))
                      .filter(o => o.value !== 'other')
                      .map(option => (
                        <label key={option.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedSecondary.includes(option.value)}
                            onChange={() => toggleSecondary(option.value)}
                          />
                          <span className="ml-2 text-sm">{option.value}</span>
                        </label>
                      ))}
                    <div className="border-t border-gray-100" />
                    <div className="px-3 py-2">
                      {!showSecondaryOther ? (
                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800 underline" onClick={() => setShowSecondaryOther(true)}>Other...</button>
                      ) : (
                        <div className="flex gap-2">
                          <input className="input flex-1" placeholder="Enter custom cartridge" value={secondaryOtherValue} onChange={(e) => setSecondaryOtherValue(e.target.value)} />
                          <button type="button" className="btn btn-secondary" onClick={addSecondaryOther}>Add</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedSecondary.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedSecondary.map((v, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs border">{v}</span>
                  ))}
                </div>
              )}
              {/* Hidden input registered to keep RHF in sync */}
              <input type="hidden" {...register('intended_calibers')} />
            </div>

            <div>
              <label className="label">Serial Number</label>
              <input
                type="text"
                {...register('serial_number')}
                className="input"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="label">Primary Type</label>
              <select
                value={selectedPrimary}
                onChange={(e) => {
                  setSelectedPrimary(e.target.value);
                  setValue('firearm_type_id', '');
                }}
                className="input"
              >
                <option value="">Select primary type...</option>
                {[...new Set(firearmTypes.map(t => t.category))].map((cat, idx) => (
                  <option key={idx} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Subtype</label>
              <select
                {...register('firearm_type_id', { required: 'Subtype is required' })}
                className="input"
                disabled={!selectedPrimary}
              >
                <option value="">{selectedPrimary ? 'Select subtype...' : 'Select primary first'}</option>
                {firearmTypes
                  .filter(t => t.category === selectedPrimary)
                  .map(type => (
                    <option key={type.id} value={type.id}>{type.subcategory}</option>
                ))}
              </select>
              {errors.firearm_type_id && (
                <p className="text-red-500 text-sm mt-1">{errors.firearm_type_id.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="label">Notes</label>
              <textarea
                {...register('notes')}
                className="input"
                rows="3"
                placeholder="Additional notes about this firearm..."
              />
            </div>

            {/* Image Uploads */}
            <div className="md:col-span-2">
              <label className="label">Images</label>
              <div className="flex gap-3 flex-wrap">
                <label className="btn btn-secondary cursor-pointer">
                  Upload Images
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    multiple
                    className="hidden"
                    onChange={(e) => handlePickFirearmFiles(e.target.files)}
                  />
                </label>
              </div>
              {!editingFirearm && Array.isArray(firearmFiles) && firearmFiles.length > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  {firearmFiles.length} file{firearmFiles.length === 1 ? '' : 's'} selected. They will be uploaded after saving.
                </p>
              )}
              {editingFirearm && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-3">
                    {(imagesByFirearm[editingFirearm.id] || []).map(file => {
                      const previewUrl = file.download_url || file.url;
                      const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
                      const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                      const thumb = isImg ? previewUrl : (file.thumbnail_url || previewUrl);
                      return (
                        <div key={file.id} className="relative group">
                          {isPdf ? (
                            <PdfThumbnail
                              fileId={file.id}
                              url={previewUrl}
                              thumbnailUrl={file.thumbnail_url}
                              filename={file.filename}
                              sizeClass="w-20 h-20"
                              onClick={() => setImagePreview({ id: file.id, url: previewUrl, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                            />
                          ) : (
                            <img
                              src={thumb}
                              alt={file.filename}
                              className="w-20 h-20 object-contain bg-white rounded shadow cursor-pointer"
                              onClick={() => setImagePreview({ id: file.id, url: previewUrl, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                            />
                          )}
                          <div className="flex justify-between mt-1 text-xs text-gray-700">
                            <button type="button" className="underline" onClick={() => setImagePreview({ id: file.id, url: previewUrl, filename: file.filename, thumbnailUrl: file.thumbnail_url })}>Open</button>
                            <button type="button" className="text-red-600 underline" onClick={() => removeFirearmImage(editingFirearm.id, file.id)}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                    {(imagesByFirearm[editingFirearm.id] || []).length === 0 && (
                      <p className="text-xs text-gray-500">No images uploaded yet for this firearm.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" className="btn btn-primary">
                {editingFirearm ? 'Update Firearm' : 'Add Firearm'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Share Recipe Modal */}
      {showShareRecipe && selectedRecipe && (
        <ShareRecipeModal onClose={() => setShowShareRecipe(false)} recipeId={selectedRecipe.id} />
      )}
      {/* Firearms List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFirearms.map(firearm => {
          const firearmRecipes = getRecipesForFirearm(firearm.id);
          const roundsFired = sessions
            .filter(s => s.firearm?.id === firearm.id)
            .reduce((sum, s) => sum + (s.rounds_fired || 0), 0);
          
          return (
          <div key={firearm.id} className="card p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Crosshair className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {firearm.manufacturer} {firearm.model}
                  </h3>
                  <p className="text-sm text-gray-600">{firearm.caliber}</p>
                    {firearm.intended_calibers && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-500">Secondary Calibers:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {JSON.parse(firearm.intended_calibers).map((caliber, index) => (
                            <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {caliber}
                            </span>
                          ))}
                </div>
              </div>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => { handleEdit(firearm); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                <Edit className="h-4 w-4" />
              </button>
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Type:</span>
                <span className="text-gray-900">
                  {firearm.firearm_type.category} - {firearm.firearm_type.subcategory}
                </span>
              </div>
              
              {/* Firearm Image Previews */}
              {(imagesByFirearm[firearm.id] || []).length > 0 && (
                <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                  {(imagesByFirearm[firearm.id] || []).map(file => {
                    const previewUrl = file.download_url || file.url;
                    const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
                    return isPdf ? (
                      <div key={file.id} className="flex-shrink-0">
                        <PdfThumbnail
                          fileId={file.id}
                          url={previewUrl}
                          thumbnailUrl={file.thumbnail_url}
                          filename={file.filename}
                          sizeClass="w-14 h-14"
                          onClick={() => setImagePreview({ id: file.id, url: previewUrl, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                        />
                      </div>
                    ) : (
                      (() => {
                        const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                        const thumb = isImg ? previewUrl : (file.thumbnail_url || previewUrl);
                        return (
                          <img
                            key={file.id}
                            src={thumb}
                            alt={file.filename}
                            title={file.filename}
                            className="w-14 h-14 object-contain bg-white rounded shadow cursor-pointer flex-shrink-0"
                            onClick={() => setImagePreview({ id: file.id, url: previewUrl, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                          />
                        );
                      })()
                    );
                  })}
                </div>
              )}

              {firearm.serial_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Serial:</span>
                  <span className="text-gray-900 font-mono">{firearm.serial_number}</span>
                </div>
              )}
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Added:</span>
                <span className="text-gray-900">
                  {new Date(firearm.created_at).toLocaleDateString()}
                </span>
              </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Rounds Fired:</span>
                  <span className="text-gray-900">{roundsFired.toLocaleString()}</span>
              </div>
            </div>
              
              {/* Recipes Section */}
              {firearmRecipes.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div
                    className="flex items-center space-x-2 mb-3 cursor-pointer select-none hover:bg-gray-100 p-1 rounded -mx-1"
                    onClick={() => setCollapsedRecipes(prev => ({ ...prev, [firearm.id]: !prev[firearm.id] }))}
                  >
                    {collapsedRecipes[firearm.id] ?? true ? <ChevronRight className="h-4 w-4 text-gray-600" /> : <ChevronDown className="h-4 w-4 text-gray-600" />}
                    <BookOpen className="h-4 w-4 text-green-600" />
                    <h4 className="text-sm font-medium text-gray-900">Recipes ({firearmRecipes.length})</h4>
                  </div>
                  {!(collapsedRecipes[firearm.id] ?? true) && (
                  <div className="space-y-2">
                    {[...firearmRecipes].sort((a,b) => (a.is_deleted?1:0) - (b.is_deleted?1:0)).map(recipe => {
                      const bulletComponent = getComponentDetails(recipe, 'bullet');
                      const powderComponent = getComponentDetails(recipe, 'powder');
                      const remainingLoads = getRemainingLoadsForRecipe(recipe.id);
                      const averageCostPerLoad = getAverageCostPerLoad(recipe.id);
                      
                      return (
                        <div 
                          key={recipe.id} 
                          className={`p-2 rounded transition-colors ${recipe.is_deleted ? 'bg-gray-100 border border-gray-200' : 'bg-gray-50 cursor-pointer hover:bg-gray-100'}`}
                          onClick={() => { if (!recipe.is_deleted) handleRecipeClick(recipe); }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                {!!recipe.is_favorite && (
                                  <Heart className="h-3 w-3 text-red-600" fill="currentColor" />
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); if (!recipe.is_deleted) handleRecipeClick(recipe); }}
                                  className={`text-sm font-medium underline text-left ${recipe.is_deleted ? 'text-gray-500' : 'text-blue-600 hover:text-blue-800'}`}
                                >
                                  {recipe.is_deleted ? `Deleted — ${recipe.name}` : recipe.name}
                                </button>
                                <ExternalLink className="h-3 w-3 text-gray-400" />
                              </div>
                              <div className="text-xs text-gray-600 mt-1 space-y-1">
                                <div>Cartridge: {cartridgeAbbrev(recipe.cartridge || 'N/A')}</div>
                                {bulletComponent && (
                                  <div>Bullet: {bulletComponent.brand} {bulletComponent.model}</div>
                                )}
                                {powderComponent && (
                                  <div>Powder: {powderComponent.brand} {powderComponent.model} ({recipe.components.find(c => c.component_type.toLowerCase() === 'powder')?.quantity}g)</div>
                                )}
                                <div className="flex justify-between items-center mt-1">
                                  <div className={`text-xs font-medium ${remainingLoads > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                    {remainingLoads} loads remaining
                                  </div>
                                  <div className="text-xs font-medium text-blue-600">
                                    ${averageCostPerLoad.toFixed(2)}/load
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              )}
            
            {firearm.notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">{firearm.notes}</p>
              </div>
            )}

              {/* Firing Sessions */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div
                  className="flex items-center space-x-2 mb-2 cursor-pointer select-none hover:bg-gray-100 p-1 rounded -mx-1"
                  onClick={() => setCollapsedFiringSessions(prev => ({ ...prev, [firearm.id]: !prev[firearm.id] }))}
                >
                  {collapsedFiringSessions[firearm.id] ?? true ? <ChevronRight className="h-4 w-4 text-gray-600" /> : <ChevronDown className="h-4 w-4 text-gray-600" />}
                  <Crosshair className="h-4 w-4 text-blue-600" />
                  <h4 className="text-sm font-medium text-gray-900">Firing Sessions ({sessions.filter(s => s.firearm?.id === firearm.id).length})</h4>
                </div>
                {!(collapsedFiringSessions[firearm.id] ?? true) && (
                <div className="space-y-2">
                  {(() => {
                    const fSessions = (sessions || [])
                      .filter(s => s.firearm?.id === firearm.id)
                      .sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
                    if (fSessions.length === 0) {
                      return null;
                    }
                    // Group by date (YYYY-MM-DD) and location
                    const groups = [];
                    const byKey = new Map();
                    for (const s of fSessions) {
                      const d = new Date(s.session_date);
                      const key = isNaN(d.getTime()) ? String(s.session_date).slice(0, 10) : d.toISOString().slice(0, 10);
                      const loc = (s.location || '').trim();
                      const groupKey = `${key}|||${loc}`;
                      if (!byKey.has(groupKey)) byKey.set(groupKey, []);
                      byKey.get(groupKey).push(s);
                    }
                    Array.from(byKey.keys()).sort((a, b) => {
                      const [da] = a.split('|||');
                      const [db] = b.split('|||');
                      return new Date(db) - new Date(da);
                    }).forEach(k => {
                      groups.push([k, byKey.get(k)]);
                    });
                    return groups.map(([groupKey, list]) => {
                      const [dateKey, locLabel] = groupKey.split('|||');
                      const total = list.reduce((sum, s) => sum + (Number(s.rounds_fired) || 0), 0);
                      const expKey = `${firearm.id}-${groupKey}`;
                      const isOpen = !!expandedSessions[expKey];
                      return (
                        <div key={groupKey} className="bg-gray-50 rounded">
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-sm flex items-center justify-between"
                            onClick={() => setExpandedSessions(prev => ({ ...prev, [expKey]: !prev[expKey] }))}
                            title="Toggle sessions"
                          >
                            <span className="flex items-start gap-2 text-left">
                              {isOpen ? <ChevronDown className="h-4 w-4 text-gray-600 mt-0.5" /> : <ChevronRight className="h-4 w-4 text-gray-600 mt-0.5" />}
                              <span>
                                <div className="text-gray-900">{formatLocalDate(dateKey)}</div>
                                {locLabel && <div className="text-xs text-gray-600">{locLabel}</div>}
                              </span>
                            </span>
                            <span className="text-gray-700">{total.toLocaleString()} rounds</span>
                          </button>
                              {isOpen && (
                            <div className="px-3 pb-2 space-y-2">
                              {list.map(s => (
                                <div key={s.id} className="text-xs text-gray-700 flex items-start justify-between gap-2">
                                  <div className="whitespace-normal break-words">
                                    {(() => {
                                      const rec = getRecipeByLoadId(s.load?.id);
                                      const label = s.load?.recipe_name || rec?.name || '—';
                                      if (rec) {
                                        return (
                                          <button
                                            type="button"
                                            className="text-blue-600 underline hover:text-blue-700"
                                            onClick={(e) => { e.stopPropagation(); handleRecipeClick(rec); }}
                                            title="Open recipe"
                                          >
                                            {label}
                                          </button>
                                        );
                                      }
                                      return <span>{label}</span>;
                                    })()}
                                  </div>
                                  <div className="shrink-0 whitespace-nowrap">{(s.rounds_fired || 0).toLocaleString()} rds</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                  {sessions.filter(s => s.firearm?.id === firearm.id).length === 0 && (
                    <div className="text-xs text-gray-500">No sessions for this firearm</div>
                  )}
                </div>
                )}
              </div>
            </div>
          );
        })}
        
        {filteredFirearms.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Crosshair className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No firearms match your search' : 'No firearms added yet'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary mt-4"
              >
                Add Your First Firearm
              </button>
            )}
          </div>
        )}
      </div>

      <ImagePreviewModal id={imagePreview?.id} url={imagePreview?.url} filename={imagePreview?.filename} thumbnailUrl={imagePreview?.thumbnailUrl} onClose={() => setImagePreview(null)} />

      {/* Shared Recipe Modal */}
      <RecipeModal show={!!(showRecipeModal && selectedRecipe)} recipe={selectedRecipe} onClose={closeRecipeModal} />
      {false && showRecipeModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">{selectedRecipe.name}</h2>
              <button
                onClick={closeRecipeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="mb-4 flex gap-3 items-center flex-wrap">
                <button onClick={openAddLoadsFromRecipe} className="btn btn-primary btn-sm">Add Loads</button>
                <button onClick={openSessionFromRecipe} className="btn btn-secondary btn-sm">Firing Session</button>
                <button onClick={() => setShowShareRecipe(true)} className="btn btn-secondary btn-sm">Share Recipe</button>
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUploadRecipe(selectedRecipe.id, e.target.files)} />
                </label>
                
              </div>
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Cartridge</label>
                  <p className="text-sm text-gray-900">{selectedRecipe.cartridge || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedRecipe.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Recipe Statistics */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-3">Recipe Statistics</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Loads Created:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {getTotalLoadsCreated(selectedRecipe.id)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Loads Remaining:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {getRemainingLoadsForRecipe(selectedRecipe.id)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Spent:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      ${getTotalSpent(selectedRecipe.id).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Average Cost per Load:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      ${getAverageCostPerLoad(selectedRecipe.id).toFixed(2)}
                    </span>
                  </div>
                </div>
                {(() => {
                  const per = getVelocityByFirearmForRecipe(selectedRecipe.id) || [];
                  if (per.length === 0) return null;
                  return (
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-1">Velocity by Firearm</div>
                      <div className="space-y-1 text-xs">
                        {per.map((r, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-gray-700 truncate pr-2">{r.label}</span>
                            <span className="text-gray-900">Avg {r.average} • ES {r.es ?? '-'} • SD {r.sd ?? '-'} • N {r.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Load Batches for this Recipe */}
              {(() => {
                const recipeLoads = (loads || []).filter(l => (l.recipe && l.recipe.id) === selectedRecipe.id);
                return (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-700">Load Batches</div>
                      <div className="text-xs text-gray-500">{recipeLoads.length} batch{recipeLoads.length === 1 ? '' : 'es'}</div>
                    </div>
                    <div className="space-y-2">
                      {recipeLoads.map((l) => (
                        <div key={l.id} className="flex justify-between items-center text-sm bg-white border rounded p-2">
                          <div>
                            <div className="font-medium text-gray-900">{l.recipe?.name || 'Recipe'}</div>
                            <div className="text-gray-600">
                              {(l.case_batch && typeof l.case_batch.firing_count !== 'undefined') ? `${l.case_batch.firing_count}x brass` : 'New brass'}
                              {l.date_created ? ` • Created ${new Date(l.date_created).toLocaleDateString()}` : ''}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-semibold ${ (l.remaining_quantity || 0) > 0 ? 'text-green-600' : 'text-gray-600' }`}>
                              {(l.remaining_quantity || 0)} remaining
                            </div>
                            <div className="text-gray-600">
                              {(() => {
                                const tc = Number(l.total_cost);
                                const qty = Number(l.quantity_created);
                                const avg = (!isNaN(tc) && !isNaN(qty) && qty > 0)
                                  ? (tc / qty)
                                  : (typeof l.computed_cost_per_round === 'number' ? l.computed_cost_per_round : Number(l.cost_per_round || 0));
                                return `$${Number(avg).toFixed(2)}/rd • $${Number(tc || (avg * (qty || 0))).toFixed(2)} total`;
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                      {recipeLoads.length === 0 && (
                        <div className="text-sm text-gray-500">No load batches yet for this recipe.</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Images */}
              <div>
                <div className="flex flex-wrap gap-3">
                  {(imagesByRecipe[selectedRecipe.id] || []).map(file => {
                    const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
                    return (
                      <div key={file.id} className="relative group">
                        {isPdf ? (
                          <div className="w-40 h-56 bg-gray-100 border rounded overflow-hidden shadow flex items-center justify-center">
                            <iframe src={file.url} title={file.filename} className="w-full h-full" />
                          </div>
                        ) : (
                          <img src={file.url} alt={file.filename} className="w-24 h-24 object-cover rounded shadow cursor-pointer" onClick={() => window.open(file.url, '_blank')} />
                        )}
                        <div className="flex justify-between mt-1 text-xs text-gray-700">
                          <button className="underline" onClick={() => window.open(file.url, '_blank')}>Open</button>
                          <button className="text-red-600 underline" onClick={() => removeRecipeImage(selectedRecipe.id, file.id)}>Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              {selectedRecipe.description && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <p className="text-sm text-gray-900">{selectedRecipe.description}</p>
                </div>
              )}

              {/* Components */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Components</label>
                
                {/* Component Summary */}
                {!loadingAvailability && componentAvailability.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-blue-900 mb-2">Component Availability Summary</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {componentAvailability.map((comp, index) => {
                        const recipeComponent = selectedRecipe.components.find(rc => 
                          comp.component.toLowerCase().includes(rc.component.brand.toLowerCase()) &&
                          comp.component.toLowerCase().includes(rc.component.model.toLowerCase())
                        );
                        const maxLoads = recipeComponent ? Math.floor(comp.available_quantity / recipeComponent.quantity) : 0;
                        
                        return (
                          <div key={index} className="flex justify-between">
                            <span className="text-blue-800">{comp.component}:</span>
                            <span className={`font-medium ${
                              maxLoads > 50 ? 'text-green-600' : 
                              maxLoads > 0 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {maxLoads} loads possible
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {loadingAvailability ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading component availability...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedRecipe.components?.map((rc, index) => {
                      const availability = componentAvailability.find(comp => 
                        comp.component.toLowerCase().includes(rc.component.brand.toLowerCase()) &&
                        comp.component.toLowerCase().includes(rc.component.model.toLowerCase())
                      );
                      
                      const maxLoadsPossible = availability ? Math.floor(availability.available_quantity / rc.quantity) : 0;
                      
                      return (
                        <div key={index} className="p-3 bg-gray-50 rounded">
                          <div className="space-y-2">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 text-lg flex items-center justify-between">
                                  <span>
                                    {rc.component_type.toLowerCase() === 'brass'
                                      ? (rc.component.head_stamp || rc.component.model || rc.component.brand)
                                      : `${rc.component.brand} ${rc.component.model}`}
                                  </span>
                                  <button className="btn btn-secondary btn-xs" title="Purchase" onClick={() => { setPurchaseComponent(rc.component); setShowPurchaseModal(true); setPurchaseForm({ quantity:'', total_cost:'', unit_cost:'', purchase_date:new Date().toISOString().split('T')[0], purchase_location:'', website:'', notes:''}); }}>
                                    <ShoppingCart className="h-4 w-4 text-white" />
                                  </button>
                                </div>
                                <div className={`text-sm uppercase tracking-wide font-medium ${
                                  rc.component_type.toLowerCase() === 'bullet' ? 'text-blue-600' :
                                  rc.component_type.toLowerCase() === 'primer' ? 'text-red-600' :
                                  rc.component_type.toLowerCase() === 'powder' ? 'text-orange-600' :
                                  rc.component_type.toLowerCase() === 'brass' ? 'text-yellow-600' :
                                  'text-gray-500'
                                }`}>
                                  {rc.component_type}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Quantity per load: {rc.quantity} {rc.unit}
                                </div>
                                {(() => {
                                  const s = recipeStats[selectedRecipe.id] || {};
                                  const list = Array.isArray(s.componentBreakdown) ? s.componentBreakdown : [];
                                  const find = list.find(cb => {
                                    try {
                                      return (cb.type || '').toLowerCase() === rc.component_type.toLowerCase() && (cb.component || '').toLowerCase().includes(rc.component.brand.toLowerCase()) && (cb.component || '').toLowerCase().includes(rc.component.model.toLowerCase());
                                    } catch {
                                      return false;
                                    }
                                  });
                                  if (!find) return null;
                                  const curUnit = (typeof find.current_unit_cost === 'number') ? find.current_unit_cost : null;
                                  const avgUnit = (typeof find.average_unit_cost === 'number') ? find.average_unit_cost : null;
                                  const qty = Number(rc.quantity || 0);
                                  const curPerLoad = curUnit != null ? (curUnit * qty) : null;
                                  const avgPerLoad = avgUnit != null ? (avgUnit * qty) : null;
                                  return (
                                    <div className="mt-1 text-xs text-gray-700">
                                      <div>Current cost per load: {curPerLoad != null ? `$${curPerLoad.toFixed(2)}` : 'NA'}</div>
                                      <div>Average cost per load: {avgPerLoad != null ? `$${avgPerLoad.toFixed(2)}` : 'NA'}</div>
                                    </div>
                                  );
                                })()}
                                
                                {/* Component-specific details */}
                                {rc.component.description && (
                                  <div className="text-sm text-gray-600 mt-1 italic">
                                    {rc.component.description}
                                  </div>
                                )}
                                
                                {/* Component-specific details */}
                                {rc.component_type.toLowerCase() === 'bullet' && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                    {rc.component.weight_grains && (
                                      <div className="text-sm text-gray-600">
                                        Weight: {rc.component.weight_grains} grains
                                      </div>
                                    )}
                                    {rc.component.length_inches && (
                                      <div className="text-sm text-gray-600">
                                        Length: {rc.component.length_inches}" 
                                      </div>
                                    )}
                                    {rc.component.caliber_inches && (
                                      <div className="text-sm text-gray-600">
                                        Caliber: {rc.component.caliber_inches}"
                                      </div>
                                    )}
                                    {rc.component.core_material && (
                                      <div className="text-sm text-gray-600">
                                        Core: {rc.component.core_material}
                                        {rc.component.core_material_other && ` (${rc.component.core_material_other})`}
                                      </div>
                                    )}
                                    {rc.component.covering && (
                                      <div className="text-sm text-gray-600">
                                        Covering: {rc.component.covering}
                                        {rc.component.covering_other && ` (${rc.component.covering_other})`}
                                      </div>
                                    )}
                                    {rc.component.shape && (
                                      <div className="text-sm text-gray-600">
                                        Shape: {rc.component.shape}
                                        {rc.component.shape_other && ` (${rc.component.shape_other})`}
                                      </div>
                                    )}
                                    {rc.component.cannelured && (
                                      <div className="text-sm text-gray-600">
                                        Cannelured: Yes
                                        {rc.component.cannelure_depth && ` (${rc.component.cannelure_depth}" depth)`}
                                      </div>
                                    )}
                                    {rc.component.intended_cartridges && (
                                      <div className="text-sm text-gray-600">
                                        Intended: {rc.component.intended_cartridges}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Primer-specific details */}
                                {rc.component_type.toLowerCase() === 'primer' && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                    {rc.component.firearm_type && (
                                      <div className="text-sm text-gray-600">
                                        Firearm Type: {rc.component.firearm_type}
                                      </div>
                                    )}
                                    {rc.component.primer_size && (
                                      <div className="text-sm text-gray-600">
                                        Size: {rc.component.primer_size}
                                      </div>
                                    )}
                                    {rc.component.magnum && (
                                      <div className="text-sm text-gray-600">
                                        Magnum: Yes
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Brass-specific details */}
                                {rc.component_type.toLowerCase() === 'brass' && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                    {rc.component.head_stamp && (
                                      <div className="text-sm text-gray-600">
                                        Head Stamp: {rc.component.head_stamp}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Powder-specific details */}
                                {rc.component_type.toLowerCase() === 'powder' && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                    {rc.component.description && (
                                      <div className="text-sm text-gray-600">
                                        {rc.component.description}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {availability && (
                              <div className="pt-2 border-t border-gray-200">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600">Available:</span>
                                    <span className="ml-1 font-medium text-gray-900">
                                      {availability.available_quantity.toLocaleString()} {availability.unit}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Max loads:</span>
                                    <span className={`ml-1 font-medium ${maxLoadsPossible > 50 ? 'text-green-600' : maxLoadsPossible > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                                      {maxLoadsPossible.toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                                                                 {rc.component_type.toLowerCase() === 'powder' && availability.unit === 'grains' && (
                                   <div className="text-xs text-gray-500 mt-1">
                                     ({(availability.available_quantity / 7000).toFixed(2)} lbs available)
                                   </div>
                                 )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Dimensions */}
              {(selectedRecipe.case_trim_length || selectedRecipe.cartridge_overall_length || selectedRecipe.seating_depth) && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Dimensions</label>
                  <div className="grid grid-cols-3 gap-4">
                    {selectedRecipe.case_trim_length && (
                      <div>
                        <label className="text-xs text-gray-600">Case Trim Length</label>
                        <p className="text-sm text-gray-900">{selectedRecipe.case_trim_length}"</p>
                      </div>
                    )}
                    {selectedRecipe.cartridge_overall_length && (
                      <div>
                        <label className="text-xs text-gray-600">Overall Length</label>
                        <p className="text-sm text-gray-900">{selectedRecipe.cartridge_overall_length}"</p>
                      </div>
                    )}
                    {selectedRecipe.seating_depth && (
                      <div>
                        <label className="text-xs text-gray-600">Seating Depth</label>
                        <p className="text-sm text-gray-900">{selectedRecipe.seating_depth}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Crimp Settings */}
              {(selectedRecipe.crimp_type || selectedRecipe.crimp_strength) && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Crimp Settings</label>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedRecipe.crimp_type && (
                      <div>
                        <label className="text-xs text-gray-600">Crimp Type</label>
                        <p className="text-sm text-gray-900">{selectedRecipe.crimp_type}</p>
                      </div>
                    )}
                    {selectedRecipe.crimp_strength && (
                      <div>
                        <label className="text-xs text-gray-600">Crimp Strength</label>
                        <p className="text-sm text-gray-900">{selectedRecipe.crimp_strength}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedRecipe.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-sm text-gray-900">{selectedRecipe.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeRecipeModal}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {showPurchaseModal && purchaseComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={()=>setShowPurchaseModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Record Purchase — {purchaseComponent.brand} {purchaseComponent.model || purchaseComponent.caliber}</h3>
              <button onClick={()=>setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Quantity</label>
                <input type="number" className="input" value={purchaseForm.quantity} onChange={(e)=>handlePurchaseCalc('quantity', e.target.value)} />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input type="date" className="input" value={purchaseForm.purchase_date} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_date:e.target.value}))} />
              </div>
              <div>
                <label className="label">Total Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.total_cost} onChange={(e)=>handlePurchaseCalc('total_cost', e.target.value)} />
              </div>
              <div>
                <label className="label">Unit Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.unit_cost} onChange={(e)=>handlePurchaseCalc('unit_cost', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Purchase Location</label>
                <input type="text" className="input" value={purchaseForm.purchase_location} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_location:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Website</label>
                <input type="url" className="input" value={purchaseForm.website} onChange={(e)=>setPurchaseForm(prev=>({...prev, website:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" value={purchaseForm.notes} onChange={(e)=>setPurchaseForm(prev=>({...prev, notes:e.target.value}))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={()=>setShowPurchaseModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPurchaseFromRecipe}>Save Purchase</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Loads From Recipe Modal (handled by shared modal) */}
      {false && showAddLoadsFromRecipe && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Loads - {selectedRecipe.name}</h3>
              <button onClick={() => setShowAddLoadsFromRecipe(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmitAddLoad(onSubmitAddLoadsFromRecipe)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Recipe</label>
                <input value={selectedRecipe.name} readOnly className="input bg-gray-100" />
              </div>
              <div className="md:col-span-2">
                <label className="label">Case Batch (Required)</label>
                <select {...registerAddLoad('case_batch_id', { required: 'Case batch is required' })} className="input" defaultValue="">
                  <option value="">Select case batch...</option>
                  {recipeCaseBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.component_name} - {batch.firing_count} firings ({batch.remaining_quantity} remaining)
                    </option>
                  ))}
                </select>
                {errorsAddLoad?.case_batch_id && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.case_batch_id.message}</p>)}
              </div>
              <div>
                <label className="label">Quantity to Create</label>
                <input type="number" className="input" placeholder="e.g., 50" {...registerAddLoad('quantity_created', { required: 'Quantity is required', min: { value: 1, message: 'Must be at least 1' } })} />
                {errorsAddLoad?.quantity_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.quantity_created.message}</p>)}
              </div>
              <div>
                <label className="label">Date Created</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerAddLoad('date_created', { required: 'Date is required' })} />
                {errorsAddLoad?.date_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.date_created.message}</p>)}
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Notes (optional)" {...registerAddLoad('notes')} />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Create Load Batch</button>
                <button type="button" onClick={() => setShowAddLoadsFromRecipe(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Firing Session From Recipe Modal (handled by shared modal) */}
      {false && showSessionFromRecipe && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New Firing Session - {selectedRecipe.name}</h3>
              <button onClick={() => setShowSessionFromRecipe(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmitSession(onSubmitSessionFromRecipe)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Select Load Batch (with remaining rounds)</label>
                <select className="input" defaultValue="" {...registerSession('load_id', { required: 'Load batch is required' })}>
                  <option value="">Select load batch...</option>
                  {loads.filter(l => l.recipe?.id === selectedRecipe.id && (l.remaining_quantity || 0) > 0).map(l => (
                    <option key={l.id} value={l.id}>
                      {l.recipe.name} • {l.remaining_quantity} remaining • {l.case_batch ? `${l.case_batch.firing_count}x brass` : 'new brass'}
                    </option>
                  ))}
                </select>
                {errorsSession?.load_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.load_id.message}</p>)}
              </div>
              <div>
                <label className="label">Firearm</label>
                <select className="input" defaultValue={selectedRecipe.firearm?.id || ''} {...registerSession('firearm_id', { required: 'Firearm is required' })}>
                  <option value="">Select firearm...</option>
                  {getCompatibleFirearmsForRecipe(selectedRecipe).map(f => (
                    <option key={f.id} value={f.id}>{f.manufacturer} {f.model} ({f.caliber})</option>
                  ))}
                </select>
                {errorsSession?.firearm_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.firearm_id.message}</p>)}
              </div>
              <div>
                <label className="label">Location</label>
                <input type="text" className="input" placeholder="e.g., Local Range" {...registerSession('location', { required: 'Location is required' })} />
                {errorsSession?.location && (<p className="text-red-500 text-sm mt-1">{errorsSession.location.message}</p>)}
              </div>
              <div>
                <label className="label">Date</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerSession('session_date', { required: 'Date is required' })} />
                {errorsSession?.session_date && (<p className="text-red-500 text-sm mt-1">{errorsSession.session_date.message}</p>)}
              </div>
              <div>
                <label className="label">Rounds Fired</label>
                <input type="number" className="input" placeholder="e.g., 10" {...registerSession('rounds_fired', { required: 'Rounds fired is required', min: { value: 1, message: 'Must be at least 1' } })} />
                {errorsSession?.rounds_fired && (<p className="text-red-500 text-sm mt-1">{errorsSession.rounds_fired.message}</p>)}
              </div>
              <div>
                <label className="label">Temperature (°F)</label>
                <input type="number" className="input" placeholder="e.g., 72" {...registerSession('temperature')} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Session notes (optional)" {...registerSession('notes')} />
              </div>

              {/* Velocity entry */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Velocity Records (fps)</label>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => appendSessionVelocity({ velocity: '' })}>
                    <Plus className="w-4 h-4 mr-1" /> Add Velocity
                  </button>
                </div>
                {fieldsSession.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                    {fieldsSession.map((field, index) => (
                      <div key={field.id} className="relative">
                        <input type="number" {...registerSession(`velocities.${index}.velocity`)} className="input text-sm pr-8" placeholder={`Shot ${index + 1}`} />
                        <button type="button" onClick={() => removeSessionVelocity(index)} className="absolute right-1 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700" title="Remove this velocity">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Click "Add Velocity" to record individual shot velocities (optional)</p>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Record Session</button>
                <button type="button" onClick={() => setShowSessionFromRecipe(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Firearms;

// Inline share modal reused from Recipes page
const ShareRecipeModal = ({ onClose, recipeId }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [message, setMessage] = useState('Check out this recipe!');

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.listVisible('');
      const myId = JSON.parse(localStorage.getItem('authUser') || '{}')?.id;
      const list = (res.data || []).filter(u => u.id !== myId);
      setUsers(list);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const send = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return toast.error('Select at least one recipient');
    try {
      for (const id of ids) {
        try {
          const up = await conversationsAPI.upsert([Number(id)]);
          const convId = up?.data?.id;
          if (convId) await conversationsAPI.sendMessage(convId, message, undefined, recipeId);
        } catch {}
      }
      toast.success('Recipe shared');
      onClose();
    } catch {
      toast.error('Failed to share');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Share Recipe</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <input className="input" placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} />
          {loading ? (
            <div className="text-sm text-gray-500">Loading users…</div>
          ) : (
            users.map(u => (
              <label key={u.id} className="flex items-center justify-between border rounded p-2">
                <div>
                  <div className="font-medium text-gray-900">{u.display_name || u.email}</div>
                  <div className="text-xs text-gray-600">{u.first_name} {u.last_name}</div>
                </div>
                <input type="checkbox" className="h-4 w-4" checked={!!selected[u.id]} onChange={() => toggle(u.id)} />
              </label>
            ))
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send}>Send</button>
        </div>
      </div>
    </div>
  );
};